import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormSelect,
  Button,
  useMultiStepForm,
} from "web-design-system";
import {
  useInvestorAddressDetails,
  useUpdateInvestorAddressDetails,
  useCreateInvestorAddressDetails,
  useLocationInfo,
  useStateOptions,
} from "creso-core/hooks";
import { AddressInfoSchema } from "creso-core/zod";
import { z } from "zod";
import { titleCase } from "creso-core/utils";
import { Client } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";

export function AddressInfo() {
  const { client } = useClientOnboarding();
  const { previousStep, updateFormData } = useMultiStepForm();

  const { mutateAsync: updateAddress } = useUpdateInvestorAddressDetails();

  const { mutateAsync: createAddress } = useCreateInvestorAddressDetails();

  const { data: addressDetails } = useInvestorAddressDetails(
    client?.investment_account ?? ""
  );

  const stateOptions = useStateOptions();

  const onSubmit = async (data: z.infer<typeof AddressInfoSchema>) => {
    const payload = {
      ...data,
      investment_account_guid: client?.investment_account ?? "",
      line2: data.line2 ?? "",
      line3: data.line3 ?? "",
      autofilled: addressDetails?.autofilled ? !isDirty : false,
      autofill_source: addressDetails?.autofill_source ?? null,
    };
    let newAddressDetails = null;
    try {
      if (addressDetails) {
        newAddressDetails = await updateAddress(payload);
      } else {
        newAddressDetails = await createAddress(payload);
      }
      updateFormData({ addressDetails: newAddressDetails });
    } catch (error) {
      handleError(error, "Failed to save address details");
    }
  };

  const form = useForm({
    resolver: zodResolver(AddressInfoSchema),
    values: {
      line1: addressDetails?.line1 || "",
      line2: addressDetails?.line2 || "",
      line3: addressDetails?.line3 || "",
      country: addressDetails?.country || "IN",
      city: addressDetails?.city || "",
      state: addressDetails?.state || "",
      postal_code: addressDetails?.postal_code || "",
    },
  });

  const { isDirty } = form.formState;

  const { refetch } = useLocationInfo(form.watch("postal_code"), false);

  const handlePostalCodeBlur = async () => {
    const isValid = await form.trigger("postal_code");
    if (isValid) {
      const result = await refetch();
      if (result.data) {
        form.setValue("state", titleCase(result.data.state), {
          shouldValidate: true,
        });
        form.setValue("city", result.data.city, { shouldValidate: true });
      }
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Address information</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="line1"
            render={({ field }) => (
              <FormInput placeholder="Address line 1" {...field} />
            )}
          />
          <FormField
            control={form.control}
            name="line2"
            render={({ field }) => (
              <FormInput
                placeholder="Address line 2"
                {...field}
                value={field.value || ""}
              />
            )}
          />
          <FormField
            control={form.control}
            name="line3"
            render={({ field }) => (
              <FormInput
                placeholder="Address line 3"
                {...field}
                value={field.value || ""}
              />
            )}
          />
          <div className="grid grid-cols-2 w-full gap-4">
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormSelect
                  label="Country"
                  {...field}
                  options={[{ label: "India", value: "IN" }]}
                />
              )}
            />
            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormInput
                  label="Postal code"
                  {...field}
                  onBlur={handlePostalCodeBlur}
                  value={field.value || ""}
                />
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormSelect label="State" {...field} options={stateOptions} />
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormInput
                  label="City or town"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
          </div>
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? addressDetails?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button type="button" variant="outline" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
